"use client";

import Input from "../common/Input";
import { useRef, useState, useEffect } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import Button from "../common/Button";
import { usePathname } from "next/navigation";
import SelectInput from "../contactUs/SelectInput";
import BouncerLoader from "../common/BouncerLoader";
import { InternalAPI } from "@/services/internalAPI/internalAPI";

const WorkWithUsForm = () => {
  const paths = usePathname();
  const fullUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${paths}`
      : '';

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      body: {
        ...prevData.body,
        from: fullUrl,
      },
    }));
  }, [fullUrl]);

  const initialFormData = {
    body: {
      name: "",
      email: "",
      phone: "",
      state: "",
      city: "",
      idState: "",
      idCity: "",
      communicationCheckbox: "",
      from: fullUrl,
      acceptTerms: false,
      comments: "",
    },
    reCaptchaToken: null as string | null,
    cvFile: null as File | null,
    typeForm: "workWithUs",
    isLoading: false,
    responseMessage: "",
    showMessage: false,
    inputFileKey: Date.now(), // Agregar una clave única para el input de archivo
  };

  const [formData, setFormData] = useState(initialFormData);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);


  const messageTimer = () => {
    setTimeout(() => {
      setFormData((prevData) => ({
        ...prevData,
        showMessage: false,
      }));
    }, 5000);
  };

  const showMessage = (message: string) => {
    setFormData((prevData) => ({
      ...prevData,
      responseMessage: message,
      showMessage: true,
    }));
    messageTimer();
  };
  
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    stateSelected: null | true = null
  ) => {
    const { name, value, files } = e.target as HTMLInputElement;
    if (e.target instanceof HTMLSelectElement) {
      const selectedText = e.target.options[e.target.selectedIndex].text;
      setFormData((prevData) => ({
        ...prevData,
        body: {
          ...prevData.body,
          [name]: selectedText,
          ...(stateSelected ? { idState: value } : { idCity: value }),
        },
      }));
    } else if (e.target.type === "file") {
        handleFileChange(e as React.ChangeEvent<HTMLInputElement>);
    } else {
      let newValue = value;
    
      if (e.target.type === "checkbox") {
        newValue = String((e.target as HTMLInputElement).checked);
      } else if (name === "phone") {
        newValue = value.replace(/\D/g, "").slice(0, 10);
      }
    
      setFormData((prevData) => ({
        ...prevData,
        body: {
          ...prevData.body,
          [name]: newValue,
        },
      }));
    }
    
  };

  const handleRecaptchaChange = (token: string | null) => {
    setFormData((prevData) => ({
      ...prevData,
      reCaptchaToken: token,
    }));
  };

  const validateFile = (file: File | null): string | null => {
    if (!file) {
      return "El CV es requerido";
    }

    const fileName = file.name.toLowerCase();
    const allowedExtensions = /(\.pdf|\.doc|\.docx)$/;
    
    if (!allowedExtensions.test(fileName)) {
      return "Solo se permiten archivos PDF o DOC";
    }
    
    if (file.size > 5 * 1024 * 1024) {
      return "El archivo no debe superar los 5MB";
    }
    
    return null;
  };
  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  
    setFormData((prevData) => ({
      ...prevData,
      cvFile: null,
      inputFileKey: Date.now(),
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    const file = files && files[0] ? files[0] : null;

    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors.cvFile;
      return newErrors;
    });

    if (file) {
      const fileError = validateFile(file);
      
      if (fileError) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          cvFile: fileError,
        }));
        resetFileInput();
        return;
      }

      setFormData((prevData) => ({
        ...prevData,
        cvFile: file,
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        cvFile: null,
      }));
    }
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.body.name.trim()) {
      newErrors.name = "El nombre es requerido";
    }
    if (!formData.body.email.trim()) {
      newErrors.email = "El correo es requerido";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.body.email)) {
      newErrors.email = "El correo no es válido";
    }
    if (!formData.body.phone.trim()) {
      newErrors.phone = "El teléfono es requerido";
    } else if (!/^\d+$/.test(formData.body.phone)) {
      newErrors.phone = "El teléfono debe contener solo números";
    }
    if (!formData.body.idState.trim()) {
      newErrors.state = "El estado es requerido";
    }
    if (!formData.body.idCity.trim()) {
      newErrors.city = "La ciudad es requerida";
    }
    if(!formData.body.acceptTerms) {
      newErrors.acceptTerms = "Términos y condiciones requeridos";
    }
    if (!formData.cvFile) {
      newErrors.cvFile = "El CV es requerido";
    }
    const fileError = validateFile(formData.cvFile);
    if (fileError) {
      newErrors.cvFile = fileError;
      
      if (formData.cvFile) {
        resetFileInput();
      }
    }

    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
  e.preventDefault();

  setFormData((prevData) => ({
    ...prevData,
    isLoading: true,
  }));

  const validationErrors = validate();
  if (Object.keys(validationErrors).length > 0) {
    setErrors(validationErrors);
    setFormData((prevData) => ({
      ...prevData,
      isLoading: false,
    }));
    return;
  }

  if (!formData.reCaptchaToken) {
    setErrors((prevErrors) => ({
      ...prevErrors,
      reCaptcha: "Por favor, verifica que no eres un robot",
    }));
    setFormData((prevData) => ({
      ...prevData,
      isLoading: false,
    }));
    return;
  }

  try {
    const formDataToSend = new FormData();
    formDataToSend.append("body", JSON.stringify(formData.body));
    formDataToSend.append("reCaptchaToken", formData.reCaptchaToken || "");
    formDataToSend.append("typeForm", formData.typeForm);

    if (formData.cvFile) {
      formDataToSend.append("cvFile", formData.cvFile);
    }

    const formResponse = await InternalAPI.forms.sendForm(formDataToSend) as { response: string };

    showMessage(formResponse.response);

    recaptchaRef.current?.reset();
    setErrors({});
    setFormData({
      ...initialFormData,
      body: {
      ...initialFormData.body,
      from: fullUrl,
      },
      cvFile: null,
      responseMessage: formResponse.response,
      showMessage: true,
      inputFileKey: Date.now(), // Cambia la clave del input de archivo
    });

  } catch (err) {
    console.error("Error al enviar el formulario.", err);
    showMessage("Ocurrió un error al enviar el formulario");

    recaptchaRef.current?.reset();
    setErrors({});
    setFormData({
      ...initialFormData,
      body: {
        ...initialFormData.body,
        from: paths,
      },
      responseMessage: "Ocurrió un error al enviar el formulario",
      showMessage: true,
    });
  }
};


  return (
    <div className="bg-gray01 relative lg:pb-20 ">
      <div className="flex flex-col items-center pb-10 container">
        <div className="flex flex-col justify-center items-center  pt-8 xl:px-16 xl:pt-16">
          <h1 className="text-black font-bold text-[27px] xl:text-[45px] text-center">
            TRABAJA CON NOSOTROS
          </h1>
          <p className="mt-5 text-[18px] xl:text-[23px] text-center">
            Ofrecemos un excelente ambiente de trabajo y oportunidades de
            crecimiento dentro de la empresa. Tenemos capacitación constante y
            contamos con el personal más calificado dentro de nuestro ramo.
            Importante: Nuestro proceso de reclutamiento es directo y sin costo.
          </p>
        </div>
        <form className="mt-8 w-full px-2 lg:px-8 space-y-4 " onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <Input
                label="Nombre*"
                name="name"
                value={formData.body.name}
                onChange={(e) => handleChange(e)}
                className="bg-white border-gray02 border w-full h-12 p-2"
              />
              {errors.name && <p className="text-red-500">{errors.name}</p>}
            </div>
            <div>
              <Input
                label="Número de teléfono*"
                name="phone"
                type="phone"
                value={formData.body.phone}
                onChange={(e) => handleChange(e)}
                className="bg-white border-gray02 border w-full h-12 p-2"
              />
              {errors.phone && <p className="text-red-500">{errors.phone}</p>}
            </div>
            <div>
              <Input
                label="Correo*"
                name="email"
                type="email"
                value={formData.body.email}
                onChange={(e) => handleChange(e)}
                className="bg-white border-gray02 border w-full h-12 p-2"
              />
              {errors.email && <p className="text-red-500">{errors.email}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <SelectInput
                label="Estado*"
                name="state"
                value={formData.body.idState}
                onChange={(e) => handleChange(e, true)}
                className="bg-white border-gray02 border w-full h-12 p-2"
              />
              {errors.state && <p className="text-red-500">{errors.state}</p>}
            </div>
            <div>
              <SelectInput
                label="Ciudad*"
                name="city"
                value={formData.body.idCity}
                onChange={(e) => handleChange(e)}
                className="bg-white border-gray02 border w-full h-12 p-2"
                slug={formData.body.idState}
              />
              {errors.city && <p className="text-red-500">{errors.city}</p>}
            </div>
            <div>
              <Input
                key={formData.inputFileKey} // Cambia la clave del input de archivo
                label="Adjuntar CV*"
                name="cvFile"
                type="file"
                accept=".pdf,.doc,.docx"
                ref={fileInputRef}
                onChange={(e) => handleChange(e)}
                className="bg-white border-gray02 border w-full h-12 p-2"
              />
              {errors.cvFile && <p className="text-red-500">{errors.cvFile}</p>}
            </div>
          </div>

          <div>
            <Input
              label="Comentarios"
              name="comments"
              value={formData.body.comments}
              onChange={(e) => handleChange(e)}
              className="bg-white border-gray02 border w-full h-12 p-2"
            />
          </div>

          <div className="flex flex-col md:flex-row md:justify-between md:items-center mt-8">
            <div className="flex-1 mx-2 mb-4 md:mb-0">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="acceptTerms"
                  checked={formData.body.acceptTerms}
                  onChange={(e) => handleChange(e)}
                />
                <span>Acepto las condiciones del políticas de privacidad.</span>
              </label>
              {errors.acceptTerms && (
                <p className="text-red-500">{errors.acceptTerms}</p>
              )}
            </div>
            {process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY && (
              <>
                <div className="flex-1 mx-2 mb-4 md:mb-0  justify-center lg:justify-start">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
                    onChange={handleRecaptchaChange}
                  />
                {errors.reCaptcha && (<p className="text-red-500 text-sm mt-2">{ errors.reCaptcha }</p>)}
                </div>
              </>
            )}
            <div className="flex-1 mx-2">
              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={formData.isLoading}
                  className={
                    formData.isLoading
                      ? "text-dark02 w-full  justify-center uppercase font-bold text-xl border-2 border-matco"
                      : "bg-matco text-dark02 w-full  justify-center border-2 uppercase font-bold text-xl hover:border-matco hover:text-matco hover:bg-transparent"
                  }
                >
                  {formData.isLoading ? <BouncerLoader /> : "Aplicar"}
                </Button>
              </div>
            </div>
          </div>
          {formData.showMessage && (
            <div className="bg-slate-300 p-4 text-center rounded-lg mt-4 w-full">
              {formData.responseMessage}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default WorkWithUsForm;
